import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useEventPublicPaths, useEventRouteParams } from '../../hooks/useEventRouteParams';
import { redirectToLogin, shouldPromptForLogin } from '../../utils/authRedirect';
import { ownerProfilePath } from '../../navigation/browseDestinations';
import type { EventClass, PublicEventData } from '../../types/event';
import type { Vehicle } from '../../types/vehicle';
import {
  entryModeDescription,
  eventJudgingEnabled,
  isPaidEntryMode,
  isPreApprovalMode,
} from '../../utils/registrationStatus';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

const EventRegistrationForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { apiQuery } = useEventRouteParams();

  const [eventData, setEventData] = useState<PublicEventData | null>(null);
  const paths = useEventPublicPaths(eventData?.event ?? null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const returnUrl = location.pathname;

  useEffect(() => {
    if (shouldPromptForLogin(isAuthenticated, authLoading, location)) {
      redirectToLogin(navigate, returnUrl, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, returnUrl, location]);

  useEffect(() => {
    if (!apiQuery || !isAuthenticated) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [evt, garage] = await Promise.all([
          apiService.getPublicEventBySlug(apiQuery),
          apiService.getUserVehicles(),
        ]);
        setEventData(evt);
        setVehicles(garage);
        if (garage.length === 1) setVehicleId(garage[0].id);
        if (eventJudgingEnabled(evt.event.judging_enabled) && evt.classes.length === 1) {
          setClassId(evt.classes[0].id);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load registration form');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiQuery, isAuthenticated]);

  const vehicleLabel = (v: Vehicle) =>
    v.nickname || `${v.year} ${v.make} ${v.model} ${v.trim}`.trim();

  const entryMode = eventData?.event.entry_mode;
  const paid = isPaidEntryMode(entryMode);
  const curated = isPreApprovalMode(entryMode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const judgingOn = eventJudgingEnabled(eventData?.event.judging_enabled);
    if (!eventData || vehicleId === '' || (judgingOn && classId === '')) {
      setError(judgingOn ? 'Select a vehicle and class' : 'Select a vehicle');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await apiService.submitRegistration({
        event_id: eventData.event.id,
        vehicle_id: Number(vehicleId),
        class_id: judgingOn ? Number(classId) : null,
        notes: notes.trim() || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <>
        <PageLoader message="Loading registration…" />
        <Footer />
      </>
    );
  }

  if (authLoading || loading) {
    return (
      <>
        <PageLoader message="Loading registration…" />
        <Footer />
      </>
    );
  }

  if (success && eventData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <div className="flex-1 max-w-lg mx-auto w-full px-4 py-12 text-center">
          <p className="kicker mb-3">Event registration</p>
          <h1 className="heading-section mb-2" data-testid="registration-success">
            {curated ? 'Application submitted' : paid ? 'Registration started' : 'Registration submitted'}
          </h1>
          <p className="text-muted mb-6 text-sm leading-relaxed">
            {curated
              ? `Your application for ${eventData.event.title} is with the organizer for review.`
              : paid
                ? `Continue from the event page when you are ready to complete entry payment.`
                : `Your vehicle is entered for ${eventData.event.title}. Your build profile stays the source of truth.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="primary" onClick={() => navigate(paths.base)}>
              View event
            </Button>
            <Button variant="ghost" onClick={() => navigate(ownerProfilePath(user?.username))}>
              Back to profile
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !eventData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-4">
        <p className="text-red-300 mb-2 text-center max-w-md text-sm">{error}</p>
        <p className="text-meta mb-4">Check your connection and try again.</p>
        <div className="flex gap-3">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
          <Link to="/events" className="btn-secondary min-h-[40px] inline-flex items-center px-4">
            Browse events
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!eventData) return null;

  const selectedVehicle = vehicles.find((v) => v.id === vehicleId);
  const selectedClass = eventData.classes.find((c) => c.id === classId);
  const judgingOn = eventJudgingEnabled(eventData.event.judging_enabled);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 sm:px-6 py-8">
        <PageBackLink label="Back to Event" to={paths.base} className="mb-4" />
        <p className="kicker mb-2">Event registration</p>
        <h1 className="heading-display mb-1">
          {curated ? 'Apply with your vehicle' : 'Register your vehicle'}
        </h1>
        <p className="text-muted text-sm mb-2 leading-relaxed">
          {eventData.event.title}
        </p>
        <p className="text-xs text-gray-500 mb-6 leading-relaxed">
          {judgingOn
            ? entryModeDescription(entryMode)
            : `${entryModeDescription(entryMode)} This event is not judged. Your build registers as a general entry.`}
        </p>

        {error && (
          <div className="alert-error mb-4" role="alert">
            {error}
          </div>
        )}

        {vehicles.length === 0 ? (
          <div className="card p-6 text-center">
            <p className="text-muted text-sm mb-4 leading-relaxed">
              Add a build to your garage before registering. You will return here after saving your vehicle.
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(ownerProfilePath(user?.username, { addVehicle: true }))}
            >
              Add a build
            </Button>
          </div>
        ) : judgingOn && eventData.classes.length === 0 ? (
          <div className="card p-6 text-center">
            <p className="text-muted text-sm">
              This event has no classes yet. Check back soon.
            </p>
          </div>
        ) : (
          <>
            {(selectedVehicle || selectedClass) && (
              <div className="card p-4 mb-5 space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Summary</p>
                {selectedVehicle ? (
                  <p className="text-sm text-white">{vehicleLabel(selectedVehicle)}</p>
                ) : null}
                {selectedClass && judgingOn ? (
                  <p className="text-sm text-gray-400">Class: {selectedClass.name}</p>
                ) : null}
                {!judgingOn ? (
                  <p className="text-sm text-gray-400">General entry</p>
                ) : null}
                {!paid ? (
                  <p className="text-xs text-gray-500">No entry fee for this event.</p>
                ) : null}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="vehicle" className="input-label">
                  Your vehicle <span className="text-red-400">*</span>
                </label>
                <select
                  id="vehicle"
                  data-testid="registration-vehicle"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : '')}
                  className="input"
                  required
                >
                  <option value="">Select a vehicle…</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {vehicleLabel(v)}
                    </option>
                  ))}
                </select>
              </div>

              {judgingOn ? (
              <div>
                <label htmlFor="class" className="input-label">
                  Class <span className="text-red-400">*</span>
                </label>
                <select
                  id="class"
                  data-testid="registration-class"
                  value={classId}
                  onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
                  className="input"
                  required
                >
                  <option value="">Select a class…</option>
                  {eventData.classes.map((c: EventClass) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              ) : null}

              <div>
                <label htmlFor="notes" className="input-label">
                  Notes <span className="text-meta font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input min-h-[88px]"
                  rows={3}
                  placeholder="Anything the organizer should know?"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                fullWidth
                className="min-h-[44px]"
                data-testid="registration-submit"
              >
                {curated ? 'Submit application' : 'Register your vehicle'}
              </Button>
            </form>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default EventRegistrationForm;
